import type {
  EnvironmentId,
  ProjectId,
  ScopedThreadRef,
  ServerAgentStatus,
  ThreadId,
  TurnId,
} from "@t3tools/contracts";
import {
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
} from "@t3tools/contracts";
import { scopedThreadKey } from "@t3tools/client-runtime";
import { serializeComposerFileLink } from "@t3tools/shared/composerTrigger";
import {
  Fragment,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  clampCollapsedComposerCursor,
  type ComposerTrigger,
  collapseExpandedComposerCursor,
  detectComposerTrigger,
  expandCollapsedComposerCursor,
  insertTranscriptAtCursor,
  replaceTextRange,
} from "../../composer-logic";
import { deriveComposerSendState, readFileAsDataUrl } from "../ChatView.logic";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type DraftId,
  type PersistedComposerImageAttachment,
  useComposerDraftStore,
  useComposerThreadDraft,
} from "../../composerDraftStore";
import { useComposerPathSearch } from "../../lib/composerPathSearchState";
import { shouldUseCompactComposerFooter } from "../composerFooterLayout";
import { type ComposerPromptEditorHandle, ComposerPromptEditor } from "../ComposerPromptEditor";
import { usePolicyControl } from "../../rpc/serverState";
import { type ComposerCommandItem, ComposerCommandMenu } from "./ComposerCommandMenu";
import { CompactComposerControlsMenu } from "./CompactComposerControlsMenu";
import type { I18nKey } from "../../i18n";
import { PolicyControlTooltip } from "../PolicyControlTooltip";
import { ComposerStatus } from "./ComposerStatus";
import { ComposerPrimaryActions } from "./ComposerPrimaryActions";
import {
  type ComposerVoiceInputControls,
  resolveVoiceInputAvailability,
} from "./ComposerVoiceButton";
import { type ComposerVoiceBarMode, ComposerVoiceRecordingBar } from "./ComposerVoiceRecordingBar";
import { describeVoiceTranscriptionFailure } from "./voiceTranscriptionFailureToast";
import { resolveComposerMenuActiveItemId } from "./composerMenuHighlight";
import { searchSlashCommandItems } from "./composerSlashCommandSearch";
import { ContextWindowMeter } from "./ContextWindowMeter";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import { AttachmentsBlock } from "./AttachmentsBlock";
import { basenameOfPath } from "../../pathEntry";
import { cn, randomUUID } from "~/lib/utils";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { ListTodoIcon, PaperclipIcon } from "lucide-react";
import type { SessionPhase, Thread } from "../../types";
import { deriveLatestContextWindowSnapshot } from "../../lib/contextWindow";
import { formatProviderSkillDisplayName } from "../../providerSkillPresentation";
import { searchProviderSkills } from "../../providerSkillSearch";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useVoiceInput } from "../../hooks/useVoiceInput";
import type { VoiceClip } from "../../voiceClips/voiceClipController";
import { useVoiceClipAudio, useVoiceClips, voiceClips } from "../../voiceClips/voiceClipStore";
import { toastManager } from "../ui/toast";
import { useTranslation } from "react-i18next";
import * as Schema from "effect/Schema";

const DEEP_WORK_INTRO_SEEN_KEY = "t3code:deep-work-intro-seen";

const IMAGE_SIZE_LIMIT_LABEL = `${Math.round(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES / (1024 * 1024))}MB`;
const FILE_SIZE_LIMIT_LABEL = `${Math.round(PROVIDER_SEND_TURN_MAX_FILE_BYTES / (1024 * 1024))}MB`;

const COMPOSER_FLOATING_LAYER_SELECTOR = [
  '[data-slot="popover-popup"]',
  '[data-slot="menu-popup"]',
  '[data-slot="select-popup"]',
  '[data-slot="combobox-popup"]',
  '[data-slot="autocomplete-popup"]',
].join(",");

const extendReplacementRangeForTrailingSpace = (
  text: string,
  rangeEnd: number,
  replacement: string,
): number => {
  if (!replacement.endsWith(" ")) {
    return rangeEnd;
  }
  return text[rangeEnd] === " " ? rangeEnd + 1 : rangeEnd;
};

function isInsideComposerFloatingLayer(element: Element): boolean {
  return element.closest(COMPOSER_FLOATING_LAYER_SELECTOR) !== null;
}

const ComposerFooterModeControls = memo(function ComposerFooterModeControls(props: {
  showPlanToggle: boolean;
  planSidebarLabel: string;
  planSidebarOpen: boolean;
  showDeepWorkToggle: boolean;
  deepWorkDisabled: boolean;
  deepWorkDisabledReason: I18nKey | null;
  deepWorkEnabled: boolean;
  onToggleDeepWork: () => void;
  onTogglePlanSidebar: () => void;
}) {
  const { t } = useTranslation();
  const planSidebarLabel = props.planSidebarLabel.toLowerCase();
  const planSidebarTooltip = props.planSidebarOpen
    ? t("chat/hideSidebar_sdafdf", { planSidebarLabel })
    : t("chat/showSidebar_tx15eq", { planSidebarLabel });

  const deepWorkTooltip = props.deepWorkDisabledReason
    ? t(props.deepWorkDisabledReason)
    : props.deepWorkEnabled
      ? t("chat/deepWorkEnabledClick_vyttgx")
      : t("chat/deepWorkDisabledClick_p0optq");

  const sections: Array<{ key: string; node: ReactNode }> = [];

  if (props.showDeepWorkToggle) {
    sections.push({
      key: "deep-work",
      node: (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                className={cn(
                  "shrink-0 whitespace-nowrap px-2 aria-disabled:cursor-not-allowed aria-disabled:opacity-40 sm:px-3",
                  props.deepWorkEnabled
                    ? "bg-blue-500/10 text-blue-400 hover:bg-blue-500/15 hover:text-blue-300"
                    : "text-muted-foreground/70 hover:text-foreground/80",
                )}
                size="sm"
                type="button"
                aria-disabled={props.deepWorkDisabled}
                onClick={props.deepWorkDisabled ? undefined : props.onToggleDeepWork}
                aria-label={deepWorkTooltip}
              />
            }
          >
            {t("chat/deepWork_6ufqdo")}
          </TooltipTrigger>
          <TooltipPopup side="top">{deepWorkTooltip}</TooltipPopup>
        </Tooltip>
      ),
    });
  }

  if (props.showPlanToggle) {
    sections.push({
      key: "plan-sidebar",
      node: (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                className={cn(
                  "shrink-0 whitespace-nowrap px-2 sm:px-3",
                  props.planSidebarOpen
                    ? "bg-blue-500/10 text-blue-400 hover:bg-blue-500/15 hover:text-blue-300"
                    : "text-muted-foreground/70 hover:text-foreground/80",
                )}
                size="sm"
                type="button"
                onClick={props.onTogglePlanSidebar}
                aria-label={planSidebarTooltip}
              />
            }
          >
            <ListTodoIcon
              className={props.planSidebarOpen ? "text-current opacity-100" : undefined}
            />
            <span className="sr-only sm:not-sr-only">{props.planSidebarLabel}</span>
          </TooltipTrigger>
          <TooltipPopup side="top">{planSidebarTooltip}</TooltipPopup>
        </Tooltip>
      ),
    });
  }

  return (
    <>
      {sections.map((section) => (
        <Fragment key={section.key}>{section.node}</Fragment>
      ))}
    </>
  );
});

const ComposerAttachFilesButton = memo(function ComposerAttachFilesButton(props: {
  disabled: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            className="shrink-0 px-2 text-muted-foreground/70 hover:text-foreground/80"
            size="sm"
            type="button"
            disabled={props.disabled}
            onClick={props.onClick}
            aria-label={t("chat/attachFiles_z860xx")}
          />
        }
      >
        <PaperclipIcon />
      </TooltipTrigger>
      <TooltipPopup side="top">{t("chat/attachFiles_z860xx")}</TooltipPopup>
    </Tooltip>
  );
});

const ComposerFooterPrimaryActions = memo(function ComposerFooterPrimaryActions(props: {
  activeContextWindow: ReturnType<typeof deriveLatestContextWindowSnapshot>;
  isUploading: boolean;
  isRunning: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  hasSendableContent: boolean;
  sendDisabled: boolean;
  sendDisabledReason: I18nKey | null;
  voiceInput: ComposerVoiceInputControls | null;
  preserveComposerFocusOnPointerDown?: boolean;
  onInterrupt: () => void;
}) {
  return (
    <>
      {props.activeContextWindow ? <ContextWindowMeter usage={props.activeContextWindow} /> : null}
      <ComposerPrimaryActions
        isRunning={props.isRunning}
        isSendBusy={props.isSendBusy}
        isConnecting={props.isConnecting}
        isEnvironmentUnavailable={props.isEnvironmentUnavailable}
        isUploading={props.isUploading}
        hasSendableContent={props.hasSendableContent}
        sendDisabled={props.sendDisabled}
        sendDisabledReason={props.sendDisabledReason}
        voiceInput={props.voiceInput}
        preserveComposerFocusOnPointerDown={props.preserveComposerFocusOnPointerDown ?? false}
        onInterrupt={props.onInterrupt}
      />
    </>
  );
});

// --------------------------------------------------------------------------
// Handle exposed to ChatView
// --------------------------------------------------------------------------

export interface ChatComposerHandle {
  focusAtEnd: () => void;
  focusAt: (cursor: number) => void;
  insertTextAtEnd: (text: string) => boolean;
  /** Replace the whole prompt with `text` and place the cursor at its end. */
  replacePrompt: (text: string) => boolean;
  readSnapshot: () => {
    value: string;
    cursor: number;
    expandedCursor: number;
  };
  /** Reset composer cursor/trigger/highlight after external prompt mutations (e.g. onSend). */
  resetCursorState: (options?: {
    cursor?: number;
    prompt?: string;
    detectTrigger?: boolean;
  }) => void;
  /** Get the current prompt, attachments and Deep Work state for use in send. */
  getSendContext: () => {
    prompt: string;
    images: ComposerImageAttachment[];
    files: ComposerFileAttachment[];
    deepWork: boolean;
  };
}

// --------------------------------------------------------------------------
// Props
// --------------------------------------------------------------------------

export interface ChatComposerProps {
  composerDraftTarget: ScopedThreadRef | DraftId;
  environmentId: EnvironmentId;
  draftId: DraftId | null;

  // Thread context
  activeThreadId: ThreadId | null;
  activeThreadEnvironmentId: EnvironmentId | undefined;
  isServerThread: boolean;
  isLocalDraftThread: boolean;

  // Session phase
  phase: SessionPhase;
  isConnecting: boolean;
  isSendBusy: boolean;
  isUploading: boolean;
  environmentUnavailable: {
    readonly label: string;
    readonly connectionState: "connecting" | "disconnected" | "error";
  } | null;

  // Plan
  activePlan: { turnId?: TurnId } | null;
  planSidebarOpen: boolean;

  agentStatus: ServerAgentStatus | null;

  // Context window
  activeThreadActivities: Thread["activities"] | undefined;

  // Misc
  resolvedTheme: "light" | "dark";
  projectId: ProjectId | null;

  // Refs the parent needs kept in sync
  promptRef: React.RefObject<string>;
  composerImagesRef: React.RefObject<ComposerImageAttachment[]>;
  composerFileAttachmentsRef: React.RefObject<ComposerFileAttachment[]>;
  composerRef: React.RefObject<ChatComposerHandle | null>;

  // Scroll
  shouldAutoScrollRef: React.RefObject<boolean>;
  scheduleStickToBottom: () => void;

  // Callbacks
  onSend: (e?: { preventDefault: () => void }) => void;
  onInterrupt: () => void;

  togglePlanSidebar: () => void;

  focusComposer: () => void;
  setComposerError: (threadId: ThreadId | null, error: string | null) => void;
  onExpandImage: (preview: ExpandedImagePreview) => void;
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export const ChatComposer = memo(function ChatComposer(props: ChatComposerProps) {
  const { t } = useTranslation();
  const {
    composerDraftTarget,
    environmentId,
    draftId,
    activeThreadId,
    activeThreadEnvironmentId: _activeThreadEnvironmentId,
    isServerThread: _isServerThread,
    isLocalDraftThread: _isLocalDraftThread,
    phase,
    isConnecting,
    isSendBusy,
    isUploading,
    environmentUnavailable,
    activePlan,
    planSidebarOpen,
    agentStatus,
    activeThreadActivities,
    resolvedTheme,
    projectId,
    promptRef,
    composerRef,
    composerImagesRef,
    composerFileAttachmentsRef,
    shouldAutoScrollRef,
    scheduleStickToBottom,
    onSend,
    onInterrupt,
    togglePlanSidebar,
    focusComposer,
    setComposerError,
    onExpandImage,
  } = props;

  // ------------------------------------------------------------------
  // Store subscriptions (prompt / images)
  // ------------------------------------------------------------------
  const composerDraft = useComposerThreadDraft(composerDraftTarget);
  const prompt = composerDraft.prompt;
  const composerImages = composerDraft.images;
  const composerFileAttachments = composerDraft.fileAttachments;
  const nonPersistedComposerImageIds = composerDraft.nonPersistedImageIds;

  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const addComposerDraftImage = useComposerDraftStore((store) => store.addImage);
  const addComposerDraftImages = useComposerDraftStore((store) => store.addImages);
  const removeComposerDraftImage = useComposerDraftStore((store) => store.removeImage);
  const addComposerDraftFileAttachments = useComposerDraftStore(
    (store) => store.addFileAttachments,
  );
  const removeComposerDraftFileAttachment = useComposerDraftStore(
    (store) => store.removeFileAttachment,
  );
  const clearComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.clearPersistedAttachments,
  );
  const syncComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.syncPersistedAttachments,
  );
  const getComposerDraft = useComposerDraftStore((store) => store.getComposerDraft);

  // ------------------------------------------------------------------
  // Deep Work
  // ------------------------------------------------------------------
  const turnControl = usePolicyControl("turn.start");
  const deepWorkControl = usePolicyControl("turn.deepWork");
  const voiceControl = usePolicyControl("voice.transcribe");
  const deepWorkEnabled = composerDraft.deepWork;
  const [deepWorkIntroOpen, setDeepWorkIntroOpen] = useState(false);
  const [deepWorkIntroSeen, setDeepWorkIntroSeen] = useLocalStorage(
    DEEP_WORK_INTRO_SEEN_KEY,
    false,
    Schema.Boolean,
  );
  const setComposerDraftDeepWork = useComposerDraftStore((store) => store.setDeepWork);
  const toggleDeepWork = useCallback(() => {
    setComposerDraftDeepWork(composerDraftTarget, !deepWorkEnabled);
  }, [composerDraftTarget, deepWorkEnabled, setComposerDraftDeepWork]);
  const requestToggleDeepWork = useCallback(() => {
    if (deepWorkControl.disabled) return;
    if (!deepWorkEnabled && !deepWorkIntroSeen) {
      setDeepWorkIntroOpen(true);
      return;
    }
    toggleDeepWork();
  }, [deepWorkControl.disabled, deepWorkEnabled, deepWorkIntroSeen, toggleDeepWork]);
  const confirmDeepWorkIntro = useCallback(() => {
    if (deepWorkControl.disabled) return;
    setDeepWorkIntroSeen(true);
    setDeepWorkIntroOpen(false);
    toggleDeepWork();
  }, [deepWorkControl.disabled, setDeepWorkIntroSeen, toggleDeepWork]);
  // ------------------------------------------------------------------
  // Context window
  // ------------------------------------------------------------------
  const activeContextWindow = useMemo(
    () => deriveLatestContextWindowSnapshot(activeThreadActivities ?? []),
    [activeThreadActivities],
  );

  // ------------------------------------------------------------------
  // Composer-local state
  // ------------------------------------------------------------------
  const [composerCursor, setComposerCursor] = useState(() =>
    collapseExpandedComposerCursor(prompt, prompt.length),
  );
  const [composerTrigger, setComposerTrigger] = useState<ComposerTrigger | null>(() =>
    detectComposerTrigger(prompt, prompt.length),
  );
  const [composerHighlightedItemId, setComposerHighlightedItemId] = useState<string | null>(null);
  const [composerHighlightedSearchKey, setComposerHighlightedSearchKey] = useState<string | null>(
    null,
  );
  const [isDragOverComposer, setIsDragOverComposer] = useState(false);
  const [isComposerFooterCompact, setIsComposerFooterCompact] = useState(false);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const isMobileViewport = useMediaQuery("max-sm");
  const isComposerCollapsedMobile = isMobileViewport && !isComposerFocused;
  const voiceTargetKey = useMemo(
    () =>
      typeof composerDraftTarget === "string"
        ? composerDraftTarget
        : scopedThreadKey(composerDraftTarget),
    [composerDraftTarget],
  );
  const composerVoiceClips = useVoiceClips(voiceTargetKey);
  const voiceInput = useVoiceInput({
    environmentId,
    targetKey: voiceTargetKey,
  });

  // ------------------------------------------------------------------
  // Refs
  // ------------------------------------------------------------------
  const composerEditorRef = useRef<ComposerPromptEditorHandle>(null);
  const composerFormRef = useRef<HTMLFormElement>(null);
  const composerSurfaceRef = useRef<HTMLDivElement>(null);
  const composerFormHeightRef = useRef(0);
  const composerSelectLockRef = useRef(false);
  const composerMenuOpenRef = useRef(false);
  const composerMenuItemsRef = useRef<ComposerCommandItem[]>([]);
  const activeComposerMenuItemRef = useRef<ComposerCommandItem | null>(null);
  const composerBlurFrameRef = useRef<number | null>(null);
  const mobileComposerExpandFrameRef = useRef<number | null>(null);
  const mobileComposerExpandReleaseFrameRef = useRef<number | null>(null);
  const mobileComposerExpandInFlightRef = useRef(false);
  const dragDepthRef = useRef(0);
  const attachFilesInputRef = useRef<HTMLInputElement>(null);

  // ------------------------------------------------------------------
  // Derived: composer send state
  // ------------------------------------------------------------------
  const composerSendState = useMemo(
    () =>
      deriveComposerSendState({
        prompt,
        imageCount: composerImages.length + composerFileAttachments.length,
      }),
    [composerFileAttachments.length, composerImages.length, prompt],
  );

  // ------------------------------------------------------------------
  // Derived: composer trigger / menu
  // ------------------------------------------------------------------
  const composerTriggerKind = composerTrigger?.kind ?? null;
  const pathTriggerQuery = composerTrigger?.kind === "path" ? composerTrigger.query : "";
  const isPathTrigger = composerTriggerKind === "path";
  const workspaceEntries = useComposerPathSearch({
    environmentId,
    projectId: isPathTrigger ? projectId : null,
    query: isPathTrigger ? pathTriggerQuery : null,
  });

  const composerMenuItems = useMemo<ComposerCommandItem[]>(() => {
    if (!composerTrigger) return [];
    if (composerTrigger.kind === "path") {
      return workspaceEntries.entries.map((entry) => ({
        id: `path:${entry.kind}:${entry.path}`,
        type: "path",
        path: entry.path,
        pathKind: entry.kind,
        label: basenameOfPath(entry.path),
        description: entry.parentPath ?? "",
      }));
    }
    if (composerTrigger.kind === "slash-command") {
      const providerSlashCommandItems = (agentStatus?.slashCommands ?? []).map((command) => ({
        id: `provider-slash-command:${command.name}`,
        type: "provider-slash-command" as const,
        command,
        label: `/${command.name}`,
        description:
          command.description ?? command.input?.hint ?? t("chat/runProviderCommand_tb4ywt"),
      }));
      const query = composerTrigger.query.trim().toLowerCase();
      if (!query) {
        return providerSlashCommandItems;
      }
      return searchSlashCommandItems(providerSlashCommandItems, query);
    }
    if (composerTrigger.kind === "skill") {
      return searchProviderSkills(agentStatus?.skills ?? [], composerTrigger.query).map(
        (skill) => ({
          id: `skill:${skill.name}`,
          type: "skill" as const,
          skill,
          label: formatProviderSkillDisplayName(skill),
          description:
            skill.shortDescription ??
            skill.description ??
            (skill.scope
              ? t("chat/skill_rrcapv", { "0": skill.scope })
              : t("chat/runProviderSkill_4xcb94")),
        }),
      );
    }
    return [];
  }, [agentStatus, composerTrigger, t, workspaceEntries.entries]);

  const composerMenuOpen = Boolean(composerTrigger);
  const composerMenuSearchKey = composerTrigger
    ? `${composerTrigger.kind}:${composerTrigger.query.trim().toLowerCase()}`
    : null;
  const activeComposerMenuItem = useMemo(() => {
    const activeItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    return composerMenuItems.find((item) => item.id === activeItemId) ?? null;
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuSearchKey,
  ]);

  composerMenuOpenRef.current = composerMenuOpen;
  composerMenuItemsRef.current = composerMenuItems;
  activeComposerMenuItemRef.current = activeComposerMenuItem;

  const nonPersistedComposerImageIdSet = useMemo(
    () => new Set(nonPersistedComposerImageIds),
    [nonPersistedComposerImageIds],
  );

  const planSidebarLabel = t("common/tasks_glumcl");
  const showPlanSidebarToggle = Boolean(activePlan || planSidebarOpen);
  const composerFooterActionLayoutKey = useMemo(() => {
    if (phase === "running") {
      return `running:${voiceInput.status}:${composerSendState.hasSendableContent}`;
    }
    return `idle:${composerSendState.hasSendableContent}:${isSendBusy}:${isConnecting}:${voiceInput.status}`;
  }, [composerSendState.hasSendableContent, isConnecting, isSendBusy, phase, voiceInput.status]);

  const isComposerMenuLoading =
    composerTriggerKind === "path" && pathTriggerQuery.length > 0 && workspaceEntries.isPending;
  const composerMenuEmptyState = useMemo(() => {
    if (composerTriggerKind === "skill") {
      return t("chat/noSkillsFoundTry_9pn362");
    }
    return composerTriggerKind === "path"
      ? t("chat/noMatchingFilesOr_6k4bxr")
      : t("chat/noMatchingCommand_guyu4d");
  }, [composerTriggerKind, t]);

  const collapsedComposerShowsStop =
    isUploading ||
    (phase === "running" && (!composerSendState.hasSendableContent || turnControl.disabled));
  const collapsedComposerPrimaryActionDisabled =
    !collapsedComposerShowsStop &&
    (turnControl.disabled || isSendBusy || isConnecting || !composerSendState.hasSendableContent);
  const collapsedComposerPrimaryActionLabel = collapsedComposerShowsStop
    ? isUploading
      ? t("chat/stopUpload_2wct7m")
      : t("chat/stopGeneration_ux7kfa")
    : t("chat/sendMessage_alwr73");

  // ------------------------------------------------------------------
  // Prompt helpers
  // ------------------------------------------------------------------
  const setPrompt = useCallback(
    (nextPrompt: string) => {
      setComposerDraftPrompt(composerDraftTarget, nextPrompt);
    },
    [composerDraftTarget, setComposerDraftPrompt],
  );

  const addComposerImage = useCallback(
    (image: ComposerImageAttachment) => {
      addComposerDraftImage(composerDraftTarget, image);
    },
    [composerDraftTarget, addComposerDraftImage],
  );

  const addComposerImagesToDraft = useCallback(
    (images: ComposerImageAttachment[]) => {
      addComposerDraftImages(composerDraftTarget, images);
    },
    [composerDraftTarget, addComposerDraftImages],
  );

  const removeComposerImageFromDraft = useCallback(
    (imageId: string) => {
      removeComposerDraftImage(composerDraftTarget, imageId);
    },
    [composerDraftTarget, removeComposerDraftImage],
  );

  const addComposerFileAttachmentsToDraft = useCallback(
    (attachments: ComposerFileAttachment[]) => {
      addComposerDraftFileAttachments(composerDraftTarget, attachments);
    },
    [addComposerDraftFileAttachments, composerDraftTarget],
  );

  const removeComposerFileAttachmentFromDraft = useCallback(
    (attachmentId: string) => {
      removeComposerDraftFileAttachment(composerDraftTarget, attachmentId);
    },
    [composerDraftTarget, removeComposerDraftFileAttachment],
  );

  // ------------------------------------------------------------------
  // Sync refs back to parent
  // ------------------------------------------------------------------
  useEffect(() => {
    promptRef.current = prompt;
    setComposerCursor((existing) => clampCollapsedComposerCursor(prompt, existing));
  }, [prompt, promptRef]);

  useEffect(() => {
    composerImagesRef.current = composerImages;
  }, [composerImages, composerImagesRef]);

  useEffect(() => {
    composerFileAttachmentsRef.current = composerFileAttachments;
  }, [composerFileAttachments, composerFileAttachmentsRef]);

  // ------------------------------------------------------------------
  // Composer menu highlight sync
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!composerMenuOpen) {
      setComposerHighlightedItemId(null);
      setComposerHighlightedSearchKey(null);
      return;
    }
    const nextActiveItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    setComposerHighlightedItemId((existing) =>
      existing === nextActiveItemId ? existing : nextActiveItemId,
    );
    setComposerHighlightedSearchKey((existing) =>
      existing === composerMenuSearchKey ? existing : composerMenuSearchKey,
    );
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuOpen,
    composerMenuSearchKey,
  ]);

  // ------------------------------------------------------------------
  // Reset compositor state on thread/draft change
  // ------------------------------------------------------------------
  useEffect(() => {
    setComposerHighlightedItemId(null);
    setComposerCursor(collapseExpandedComposerCursor(promptRef.current, promptRef.current.length));
    setComposerTrigger(detectComposerTrigger(promptRef.current, promptRef.current.length));
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
  }, [draftId, activeThreadId, promptRef]);

  // ------------------------------------------------------------------
  // Footer compact layout observation
  // ------------------------------------------------------------------
  useLayoutEffect(() => {
    const composerForm = composerFormRef.current;
    if (!composerForm) return;
    const measureComposerFormWidth = () => composerForm.clientWidth;
    const measureFooterCompactness = () =>
      shouldUseCompactComposerFooter(measureComposerFormWidth());

    composerFormHeightRef.current = composerForm.getBoundingClientRect().height;
    setIsComposerFooterCompact(measureFooterCompactness());
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      const nextFooterCompact = measureFooterCompactness();
      setIsComposerFooterCompact((previous) =>
        previous === nextFooterCompact ? previous : nextFooterCompact,
      );
      const nextHeight = entry.contentRect.height;
      const previousHeight = composerFormHeightRef.current;
      composerFormHeightRef.current = nextHeight;
      if (previousHeight > 0 && Math.abs(nextHeight - previousHeight) < 0.5) return;
      if (!shouldAutoScrollRef.current) return;
      scheduleStickToBottom();
    });

    observer.observe(composerForm);
    return () => {
      observer.disconnect();
    };
  }, [activeThreadId, composerFooterActionLayoutKey, scheduleStickToBottom, shouldAutoScrollRef]);

  // ------------------------------------------------------------------
  // Image persist effect
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (composerImages.length === 0) {
        clearComposerDraftPersistedAttachments(composerDraftTarget);
        return;
      }
      const getPersistedAttachmentsForThread = () =>
        getComposerDraft(composerDraftTarget)?.persistedAttachments ?? [];
      try {
        const currentPersistedAttachments = getPersistedAttachmentsForThread();
        const existingPersistedById = new Map(
          currentPersistedAttachments.map((attachment) => [attachment.id, attachment]),
        );
        const stagedAttachmentById = new Map<string, PersistedComposerImageAttachment>();
        await Promise.all(
          composerImages.map(async (image) => {
            try {
              const dataUrl = await readFileAsDataUrl(image.file);
              stagedAttachmentById.set(image.id, {
                id: image.id,
                name: image.name,
                mimeType: image.mimeType,
                sizeBytes: image.sizeBytes,
                dataUrl,
              });
            } catch {
              const existingPersisted = existingPersistedById.get(image.id);
              if (existingPersisted) {
                stagedAttachmentById.set(image.id, existingPersisted);
              }
            }
          }),
        );
        const serialized = Array.from(stagedAttachmentById.values());
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(composerDraftTarget, serialized);
      } catch {
        const currentImageIds = new Set(composerImages.map((image) => image.id));
        const fallbackPersistedAttachments = getPersistedAttachmentsForThread();
        const fallbackPersistedIds: Array<string> = [];
        for (const attachment of fallbackPersistedAttachments) {
          if (currentImageIds.has(attachment.id)) {
            fallbackPersistedIds.push(attachment.id);
          }
        }
        const fallbackPersistedIdSet = new Set(fallbackPersistedIds);
        const fallbackAttachments = fallbackPersistedAttachments.filter((attachment) =>
          fallbackPersistedIdSet.has(attachment.id),
        );
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(composerDraftTarget, fallbackAttachments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    composerDraftTarget,
    clearComposerDraftPersistedAttachments,
    composerImages,
    getComposerDraft,
    syncComposerDraftPersistedAttachments,
  ]);

  // ------------------------------------------------------------------
  // Callbacks: prompt change
  // ------------------------------------------------------------------
  const onPromptChange = useCallback(
    (
      nextPrompt: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
    ) => {
      setComposerError(activeThreadId, null);
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
      );
    },
    [activeThreadId, promptRef, setPrompt, setComposerError],
  );

  // ------------------------------------------------------------------
  // Callbacks: prompt replacement / menu
  // ------------------------------------------------------------------
  const applyPromptReplacement = useCallback(
    (
      rangeStart: number,
      rangeEnd: number,
      replacement: string,
      options?: { expectedText?: string; focusEditorAfterReplace?: boolean; cursor?: number },
    ): boolean => {
      const currentText = promptRef.current;
      const safeStart = Math.max(0, Math.min(currentText.length, rangeStart));
      const safeEnd = Math.max(safeStart, Math.min(currentText.length, rangeEnd));
      if (
        options?.expectedText !== undefined &&
        currentText.slice(safeStart, safeEnd) !== options.expectedText
      ) {
        return false;
      }
      const next = replaceTextRange(promptRef.current, rangeStart, rangeEnd, replacement);
      const nextCursor = collapseExpandedComposerCursor(next.text, options?.cursor ?? next.cursor);
      const nextExpandedCursor = expandCollapsedComposerCursor(next.text, nextCursor);
      setComposerError(activeThreadId, null);
      promptRef.current = next.text;
      setPrompt(next.text);
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTrigger(next.text, nextExpandedCursor));
      if (options?.focusEditorAfterReplace !== false) {
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAt(nextCursor);
        });
      }
      return true;
    },
    [activeThreadId, promptRef, setPrompt, setComposerError],
  );

  const readComposerSnapshot = useCallback((): {
    value: string;
    cursor: number;
    expandedCursor: number;
  } => {
    const editorSnapshot = composerEditorRef.current?.readSnapshot();
    if (editorSnapshot) {
      return editorSnapshot;
    }
    return {
      value: promptRef.current,
      cursor: composerCursor,
      expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
    };
  }, [composerCursor, promptRef]);

  const resolveActiveComposerTrigger = useCallback((): {
    snapshot: { value: string; cursor: number; expandedCursor: number };
    trigger: ComposerTrigger | null;
  } => {
    const snapshot = readComposerSnapshot();
    return {
      snapshot,
      trigger: detectComposerTrigger(snapshot.value, snapshot.expandedCursor),
    };
  }, [readComposerSnapshot]);

  const onSelectComposerItem = useCallback(
    (item: ComposerCommandItem) => {
      if (composerSelectLockRef.current) return;
      composerSelectLockRef.current = true;
      window.requestAnimationFrame(() => {
        composerSelectLockRef.current = false;
      });
      const { snapshot, trigger } = resolveActiveComposerTrigger();
      if (!trigger) return;
      if (item.type === "path") {
        const replacement = `${serializeComposerFileLink(item.path)} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "provider-slash-command") {
        const replacement = `/${item.command.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "skill") {
        const replacement = `$${item.skill.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
    },
    [applyPromptReplacement, resolveActiveComposerTrigger],
  );

  const onComposerMenuItemHighlighted = useCallback(
    (itemId: string | null) => {
      setComposerHighlightedItemId(itemId);
      setComposerHighlightedSearchKey(composerMenuSearchKey);
    },
    [composerMenuSearchKey],
  );

  const nudgeComposerMenuHighlight = useCallback(
    (key: "ArrowDown" | "ArrowUp") => {
      if (composerMenuItems.length === 0) return;
      const highlightedIndex = composerMenuItems.findIndex(
        (item) => item.id === composerHighlightedItemId,
      );
      const normalizedIndex =
        highlightedIndex >= 0 ? highlightedIndex : key === "ArrowDown" ? -1 : 0;
      const offset = key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (normalizedIndex + offset + composerMenuItems.length) % composerMenuItems.length;
      const nextItem = composerMenuItems[nextIndex];
      setComposerHighlightedItemId(nextItem?.id ?? null);
    },
    [composerHighlightedItemId, composerMenuItems],
  );

  const blurMobileComposerAfterSend = useCallback(() => {
    if (!isMobileViewport) return;
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    setIsComposerFocused(false);
  }, [isMobileViewport]);

  const shouldBlurMobileComposerOnSubmit = useCallback(() => {
    if (!isMobileViewport) return false;
    if (isSendBusy || isConnecting) return false;
    return composerSendState.hasSendableContent;
  }, [composerSendState.hasSendableContent, isConnecting, isMobileViewport, isSendBusy]);

  const sendComposer = useCallback(
    (event?: { preventDefault: () => void }) => {
      if (turnControl.disabled) {
        event?.preventDefault();
        return;
      }
      onSend(event);
      if (shouldBlurMobileComposerOnSubmit()) {
        blurMobileComposerAfterSend();
      }
    },
    [blurMobileComposerAfterSend, onSend, shouldBlurMobileComposerOnSubmit, turnControl.disabled],
  );
  const sendComposerRef = useRef(sendComposer);
  useEffect(() => {
    sendComposerRef.current = sendComposer;
  }, [sendComposer]);

  const insertVoiceTranscript = useCallback(
    (transcript: string, options?: { focusEditorAfterReplace?: boolean }): boolean => {
      const snapshot = readComposerSnapshot();
      const insertion = insertTranscriptAtCursor(
        snapshot.value,
        snapshot.expandedCursor,
        transcript,
      );
      if (!insertion) return false;
      return applyPromptReplacement(insertion.insertAt, insertion.insertAt, insertion.replacement, {
        cursor: insertion.cursor,
        ...options,
      });
    },
    [applyPromptReplacement, readComposerSnapshot],
  );

  const sendAfterVoiceRef = useRef(false);
  const voiceTargetKeyRef = useRef(voiceTargetKey);
  useEffect(() => {
    voiceTargetKeyRef.current = voiceTargetKey;
    sendAfterVoiceRef.current = false;
  }, [voiceTargetKey]);

  useEffect(() => {
    let earlierClipInFlight = false;
    for (const clip of composerVoiceClips) {
      if (clip.status === "recording" || clip.status === "transcribing") {
        earlierClipInFlight = true;
        continue;
      }
      if (clip.status !== "transcribed" || earlierClipInFlight) continue;
      const targetKey = clip.targetKey;
      void voiceClips.consumeTranscript(clip.id, (transcript) => {
        if (voiceTargetKeyRef.current !== targetKey) return false;
        return insertVoiceTranscript(transcript, {
          focusEditorAfterReplace: !sendAfterVoiceRef.current,
        });
      });
    }
    if (!sendAfterVoiceRef.current) return;
    if (composerVoiceClips.some((clip) => clip.status === "failed")) {
      sendAfterVoiceRef.current = false;
      return;
    }
    if (composerVoiceClips.length === 0 && voiceInput.status === "idle") {
      sendAfterVoiceRef.current = false;
      sendComposerRef.current();
    }
  }, [composerVoiceClips, insertVoiceTranscript, voiceInput.status]);

  const voiceClipStatusesRef = useRef(new Map<string, VoiceClip["status"]>());
  const announcedVoiceClipFailuresRef = useRef(new Set<string>());
  useEffect(() => {
    const previous = voiceClipStatusesRef.current;
    const next = new Map<string, VoiceClip["status"]>();
    for (const clip of composerVoiceClips) {
      next.set(clip.id, clip.status);
      if (clip.status !== "failed") continue;
      const previousStatus = previous.get(clip.id);
      const shouldAnnounce =
        previousStatus === undefined
          ? !announcedVoiceClipFailuresRef.current.has(clip.id)
          : previousStatus !== "failed";
      if (!shouldAnnounce) continue;
      announcedVoiceClipFailuresRef.current.add(clip.id);
      const toast = describeVoiceTranscriptionFailure(clip.failure);
      toastManager.add({
        type: toast.type,
        title: t(toast.title),
        ...(toast.description === null ? {} : { description: t(toast.description) }),
      });
    }
    voiceClipStatusesRef.current = next;
  }, [composerVoiceClips, t]);

  const submitComposer = useCallback(
    (event?: { preventDefault: () => void }) => {
      if (turnControl.disabled) {
        event?.preventDefault();
        return;
      }
      if (voiceInput.status === "recording") {
        event?.preventDefault();
        sendAfterVoiceRef.current = true;
        void voiceInput.stop();
        return;
      }
      if (voiceInput.status !== "idle") {
        event?.preventDefault();
        return;
      }
      if (composerVoiceClips.length > 0) {
        event?.preventDefault();
        if (voiceControl.disabled) return;
        sendAfterVoiceRef.current = true;
        for (const clip of composerVoiceClips) {
          if (clip.status === "pending" || clip.status === "failed") {
            void voiceClips.transcribe(clip.id);
          }
        }
        return;
      }
      sendComposer(event);
    },
    [
      composerVoiceClips,
      sendComposer,
      turnControl.disabled,
      voiceControl.disabled,
      voiceInput.status,
      voiceInput.stop,
    ],
  );

  const hasPendingVoiceClips = composerVoiceClips.length > 0;
  const voiceInputControls = useMemo<ComposerVoiceInputControls>(
    () => ({
      status: voiceInput.status,
      hasPendingClips: hasPendingVoiceClips,
      availability: resolveVoiceInputAvailability(voiceInput.supported),
      disabled: voiceControl.disabled || isConnecting || environmentUnavailable !== null,
      disabledReason: voiceControl.reason,
      onStart: voiceInput.start,
      onStop: () => void voiceInput.stop(),
    }),
    [
      environmentUnavailable,
      hasPendingVoiceClips,
      isConnecting,
      voiceControl.disabled,
      voiceControl.reason,
      voiceInput.start,
      voiceInput.status,
      voiceInput.stop,
      voiceInput.supported,
    ],
  );
  const voiceBarClip = composerVoiceClips[0] ?? null;
  const voiceBarMode: ComposerVoiceBarMode =
    voiceInput.status === "recording"
      ? "recording"
      : voiceBarClip?.status === "failed" || voiceBarClip?.status === "pending"
        ? "recorded"
        : "transcribing";
  const voiceBarAudio = useVoiceClipAudio(voiceBarMode === "recorded" ? voiceBarClip : null);
  const isVoiceBarActive = voiceBarMode === "recording" || voiceBarClip !== null;
  const cancelVoiceBar = useCallback(() => {
    if (voiceInput.status === "recording") {
      voiceInput.cancel();
      return;
    }
    if (!voiceBarClip) return;
    sendAfterVoiceRef.current = false;
    void voiceClips.discard(voiceBarClip.id);
  }, [voiceBarClip, voiceInput]);
  const expandMobileComposer = useCallback(() => {
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    if (mobileComposerExpandFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
    }
    if (mobileComposerExpandReleaseFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
    }
    mobileComposerExpandInFlightRef.current = true;
    setIsComposerFocused(true);
    mobileComposerExpandFrameRef.current = window.requestAnimationFrame(() => {
      mobileComposerExpandFrameRef.current = null;
      composerEditorRef.current?.focusAtEnd();
      mobileComposerExpandReleaseFrameRef.current = window.requestAnimationFrame(() => {
        mobileComposerExpandReleaseFrameRef.current = null;
        mobileComposerExpandInFlightRef.current = false;
      });
    });
  }, []);

  // ------------------------------------------------------------------
  // Callbacks: command key
  // ------------------------------------------------------------------
  const onComposerCommandKey = (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
  ) => {
    const { trigger } = resolveActiveComposerTrigger();
    const menuIsActive = composerMenuOpenRef.current || trigger !== null;
    if (menuIsActive) {
      const currentItems = composerMenuItemsRef.current;
      const selectedItem = activeComposerMenuItemRef.current ?? currentItems[0];
      if (key === "ArrowDown" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowDown");
        return true;
      }
      if (key === "ArrowUp" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowUp");
        return true;
      }
      if ((key === "Enter" || key === "Tab") && selectedItem) {
        onSelectComposerItem(selectedItem);
        return true;
      }
    }
    if (key === "Enter" && !event.shiftKey) {
      submitComposer();
      return true;
    }
    return false;
  };

  // ------------------------------------------------------------------
  // Callbacks: attachments
  // ------------------------------------------------------------------
  const addComposerAttachments = (files: File[]) => {
    if (!activeThreadId || files.length === 0) return;
    const nextImages: ComposerImageAttachment[] = [];
    const nextFiles: ComposerFileAttachment[] = [];
    let nextAttachmentCount =
      composerImagesRef.current.length + composerFileAttachmentsRef.current.length;
    let error: string | null = null;
    for (const file of files) {
      if (file.size > PROVIDER_SEND_TURN_MAX_FILE_BYTES) {
        error = t("chat/exceedsTheAttachmentLimit_abosjh", {
          "0": file.name,
          FILE_SIZE_LIMIT_LABEL,
        });
        continue;
      }
      if (nextAttachmentCount >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
        error = t("chat/youCanAttachUp_6hie93", { PROVIDER_SEND_TURN_MAX_ATTACHMENTS });
        break;
      }
      if (file.type.startsWith("image/") && file.size <= PROVIDER_SEND_TURN_MAX_IMAGE_BYTES) {
        const previewUrl = URL.createObjectURL(file);
        nextImages.push({
          type: "image",
          id: randomUUID(),
          name: file.name || "image",
          mimeType: file.type,
          sizeBytes: file.size,
          previewUrl,
          file,
        });
      } else {
        nextFiles.push({
          type: "file",
          id: randomUUID(),
          name: file.name || "attachment",
          mimeType: file.type,
          sizeBytes: file.size,
          file,
        });
        if (file.type.startsWith("image/")) {
          error = t("chat/exceedsTheImagePreview_yoj7qh", {
            "0": file.name,
            IMAGE_SIZE_LIMIT_LABEL,
          });
        }
      }
      nextAttachmentCount += 1;
    }
    if (nextImages.length === 1 && nextImages[0]) {
      addComposerImage(nextImages[0]);
    } else if (nextImages.length > 1) {
      addComposerImagesToDraft(nextImages);
    }
    if (nextFiles.length > 0) {
      addComposerFileAttachmentsToDraft(nextFiles);
    }
    setComposerError(activeThreadId, error);
  };

  const removeComposerImage = (imageId: string) => {
    removeComposerImageFromDraft(imageId);
  };

  const removeComposerFileAttachment = (attachmentId: string) => {
    removeComposerFileAttachmentFromDraft(attachmentId);
  };

  // ------------------------------------------------------------------
  // Callbacks: file picker / paste / drag
  // ------------------------------------------------------------------
  const openAttachFilesPicker = () => {
    attachFilesInputRef.current?.click();
  };

  const onAttachFilesInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const files = Array.from(input.files ?? []);
    input.value = "";
    addComposerAttachments(files);
    focusComposer();
  };

  const onComposerPaste = (event: React.ClipboardEvent<HTMLElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    addComposerAttachments(files);
  };

  const onComposerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOverComposer(true);
  };

  const onComposerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragOverComposer(true);
  };

  const onComposerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOverComposer(false);
    }
  };

  const onComposerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
    const files = Array.from(event.dataTransfer.files);
    addComposerAttachments(files);
    focusComposer();
  };
  const handleInterruptPrimaryAction = useCallback(() => {
    void onInterrupt();
  }, [onInterrupt]);
  const scheduleComposerCollapseCheck = useCallback(() => {
    if (!isMobileViewport) {
      return;
    }
    if (mobileComposerExpandInFlightRef.current) {
      return;
    }
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
    }
    composerBlurFrameRef.current = window.requestAnimationFrame(() => {
      composerBlurFrameRef.current = null;
      if (mobileComposerExpandInFlightRef.current) {
        return;
      }
      const composerSurface = composerSurfaceRef.current;
      const activeElement = document.activeElement;
      if (activeElement instanceof Element && isInsideComposerFloatingLayer(activeElement)) {
        return;
      }
      if (
        composerSurface &&
        activeElement instanceof Node &&
        composerSurface.contains(activeElement)
      ) {
        return;
      }
      setIsComposerFocused(false);
    });
  }, [isMobileViewport]);

  useEffect(() => {
    return () => {
      if (composerBlurFrameRef.current !== null) {
        window.cancelAnimationFrame(composerBlurFrameRef.current);
      }
      if (mobileComposerExpandFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
      }
      if (mobileComposerExpandReleaseFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // Imperative handle
  // ------------------------------------------------------------------
  useImperativeHandle(
    composerRef,
    () => ({
      focusAtEnd: () => {
        composerEditorRef.current?.focusAtEnd();
      },
      focusAt: (cursor: number) => {
        composerEditorRef.current?.focusAt(cursor);
      },
      insertTextAtEnd: (text: string) => {
        if (text.length === 0 || isConnecting || environmentUnavailable !== null) {
          return false;
        }
        const rangeEnd = promptRef.current.length;
        return applyPromptReplacement(rangeEnd, rangeEnd, text);
      },
      replacePrompt: (text: string) => {
        if (isConnecting || environmentUnavailable !== null) {
          return false;
        }
        return applyPromptReplacement(0, promptRef.current.length, text);
      },
      readSnapshot: () => {
        return readComposerSnapshot();
      },
      resetCursorState: (options?: {
        cursor?: number;
        prompt?: string;
        detectTrigger?: boolean;
      }) => {
        const promptForState = options?.prompt ?? promptRef.current;
        const cursor = clampCollapsedComposerCursor(promptForState, options?.cursor ?? 0);
        setComposerHighlightedItemId(null);
        setComposerCursor(cursor);
        setComposerTrigger(
          options?.detectTrigger
            ? detectComposerTrigger(
                promptForState,
                expandCollapsedComposerCursor(promptForState, cursor),
              )
            : null,
        );
      },
      getSendContext: () => ({
        prompt: promptRef.current,
        images: composerImagesRef.current,
        files: composerFileAttachmentsRef.current,
        deepWork: !deepWorkControl.disabled && deepWorkEnabled,
      }),
    }),
    [
      promptRef,
      composerImagesRef,
      composerFileAttachmentsRef,
      isConnecting,
      environmentUnavailable,
      applyPromptReplacement,
      readComposerSnapshot,
      deepWorkEnabled,
      deepWorkControl.disabled,
    ],
  );

  // Render
  // ------------------------------------------------------------------
  return (
    <>
      <form
        ref={composerFormRef}
        onSubmit={submitComposer}
        className="mx-auto w-full min-w-0 max-w-208"
        data-chat-composer-form="true"
      >
        <ComposerStatus />
        <input
          ref={attachFilesInputRef}
          type="file"
          multiple
          className="hidden"
          data-chat-composer-attach-input="true"
          onChange={onAttachFilesInputChange}
        />
        <div
          className="group rounded-[22px] p-px transition-colors duration-200"
          onDragEnter={onComposerDragEnter}
          onDragOver={onComposerDragOver}
          onDragLeave={onComposerDragLeave}
          onDrop={onComposerDrop}
        >
          <div
            ref={composerSurfaceRef}
            data-chat-composer-mobile-collapsed={isComposerCollapsedMobile ? "true" : "false"}
            className={cn(
              "rounded-[20px] border bg-card transition-colors duration-200 has-focus-visible:border-ring/45",
              isDragOverComposer ? "border-primary/70 bg-accent/30" : "border-border",
              environmentUnavailable ? "opacity-75" : null,
            )}
            onFocusCapture={(event) => {
              const activeElement = event.target;
              if (
                isComposerCollapsedMobile &&
                activeElement instanceof HTMLElement &&
                activeElement.closest('[data-chat-composer-collapsed-controls="true"]')
              ) {
                return;
              }
              if (composerBlurFrameRef.current !== null) {
                window.cancelAnimationFrame(composerBlurFrameRef.current);
                composerBlurFrameRef.current = null;
              }
              setIsComposerFocused(true);
            }}
            onBlurCapture={() => {
              scheduleComposerCollapseCheck();
            }}
          >
            {isComposerCollapsedMobile ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <button
                  type="button"
                  className={cn(
                    "min-w-0 flex-1 truncate bg-transparent p-0 text-left text-[14px] focus:outline-none",
                    prompt.trim() ? "text-foreground" : "text-muted-foreground/35",
                  )}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={expandMobileComposer}
                  aria-label={t("chat/expandComposer_89tdu9")}
                >
                  {prompt.trim() || t("chat/describeATaskOr_473rzh")}
                </button>
                <PolicyControlTooltip
                  reason={collapsedComposerShowsStop ? null : turnControl.reason}
                >
                  <button
                    type="button"
                    className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/90 text-primary-foreground disabled:opacity-30"
                    disabled={collapsedComposerPrimaryActionDisabled}
                    aria-label={collapsedComposerPrimaryActionLabel}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (collapsedComposerShowsStop) handleInterruptPrimaryAction();
                      else submitComposer();
                    }}
                  >
                    {collapsedComposerShowsStop ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <rect x="2" y="2" width="8" height="8" rx="1.5" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M8 3L8 13M8 3L4 7M8 3L12 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </PolicyControlTooltip>
              </div>
            ) : null}

            <div
              className={cn(
                "relative px-3 pt-3.5 pb-2 sm:px-4 sm:pt-4",
                isComposerCollapsedMobile && "hidden",
              )}
            >
              {composerMenuOpen && (
                <div className="absolute inset-x-0 bottom-full z-20 mb-2">
                  <ComposerCommandMenu
                    items={composerMenuItems}
                    isLoading={isComposerMenuLoading}
                    triggerKind={composerTriggerKind}
                    emptyStateText={composerMenuEmptyState}
                    activeItemId={activeComposerMenuItem?.id ?? null}
                    onHighlightedItemChange={onComposerMenuItemHighlighted}
                    onSelect={onSelectComposerItem}
                  />
                </div>
              )}

              {!isComposerCollapsedMobile &&
                (composerImages.length > 0 || composerFileAttachments.length > 0) && (
                  <AttachmentsBlock
                    attachments={[...composerImages, ...composerFileAttachments]}
                    variant="composer"
                    onExpandImage={onExpandImage}
                    onRemove={(attachment) =>
                      attachment.type === "image"
                        ? removeComposerImage(attachment.id)
                        : removeComposerFileAttachment(attachment.id)
                    }
                    unpersistedImageIds={nonPersistedComposerImageIdSet}
                  />
                )}

              <div className="relative">
                <ComposerPromptEditor
                  editorRef={composerEditorRef}
                  value={prompt}
                  cursor={composerCursor}
                  skills={agentStatus?.skills ?? []}
                  onChange={onPromptChange}
                  onCommandKeyDown={onComposerCommandKey}
                  onPaste={onComposerPaste}
                  placeholder={
                    environmentUnavailable
                      ? environmentUnavailable.connectionState === "connecting"
                        ? t("common/isConnecting_x4p588", {
                            "0": environmentUnavailable.label,
                          })
                        : t("common/isDisconnected_57ro2l", {
                            "0": environmentUnavailable.label,
                          })
                      : t("chat/describeATaskOr_473rzh")
                  }
                  disabled={isConnecting || environmentUnavailable !== null}
                />
              </div>
            </div>

            {/* Bottom toolbar */}
            {isComposerCollapsedMobile ? null : (
              <div
                data-chat-composer-footer="true"
                data-chat-composer-footer-compact={isComposerFooterCompact ? "true" : "false"}
                className={cn(
                  "flex min-w-0 flex-nowrap items-center justify-between gap-2 overflow-visible px-2.5 pb-2.5 sm:px-3 sm:pb-3",
                  isComposerFooterCompact ? "gap-1.5" : "gap-2 sm:gap-0",
                )}
              >
                {isVoiceBarActive ? (
                  <ComposerVoiceRecordingBar
                    mode={voiceBarMode}
                    stream={voiceInput.stream}
                    audio={voiceBarAudio}
                    durationMs={voiceBarClip?.durationMs ?? null}
                    theme={resolvedTheme}
                    onCancel={cancelVoiceBar}
                  />
                ) : (
                  <div className="-m-1 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <ComposerAttachFilesButton
                      disabled={activeThreadId === null}
                      onClick={openAttachFilesPicker}
                    />

                    {isComposerFooterCompact ? (
                      <CompactComposerControlsMenu
                        activePlan={showPlanSidebarToggle}
                        planSidebarLabel={planSidebarLabel}
                        planSidebarOpen={planSidebarOpen}
                        showDeepWorkToggle={deepWorkControl.visible}
                        deepWorkDisabled={deepWorkControl.disabled}
                        deepWorkDisabledReason={deepWorkControl.reason}
                        deepWorkEnabled={deepWorkEnabled}
                        onToggleDeepWork={requestToggleDeepWork}
                        onTogglePlanSidebar={togglePlanSidebar}
                      />
                    ) : (
                      <>
                        <ComposerFooterModeControls
                          showPlanToggle={showPlanSidebarToggle}
                          planSidebarLabel={planSidebarLabel}
                          planSidebarOpen={planSidebarOpen}
                          showDeepWorkToggle={deepWorkControl.visible}
                          deepWorkDisabled={deepWorkControl.disabled}
                          deepWorkDisabledReason={deepWorkControl.reason}
                          deepWorkEnabled={deepWorkEnabled}
                          onToggleDeepWork={requestToggleDeepWork}
                          onTogglePlanSidebar={togglePlanSidebar}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Right side: send / stop button */}
                <div
                  data-chat-composer-actions="right"
                  className="flex shrink-0 flex-nowrap items-center justify-end gap-2"
                >
                  <ComposerFooterPrimaryActions
                    activeContextWindow={isVoiceBarActive ? null : activeContextWindow}
                    isRunning={phase === "running"}
                    isSendBusy={isSendBusy}
                    isConnecting={isConnecting}
                    isEnvironmentUnavailable={environmentUnavailable !== null}
                    isUploading={isUploading}
                    hasSendableContent={composerSendState.hasSendableContent}
                    sendDisabled={turnControl.disabled}
                    sendDisabledReason={turnControl.reason}
                    voiceInput={voiceControl.visible ? voiceInputControls : null}
                    preserveComposerFocusOnPointerDown={isMobileViewport}
                    onInterrupt={handleInterruptPrimaryAction}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
      <AlertDialog open={deepWorkIntroOpen} onOpenChange={setDeepWorkIntroOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("chat/enableDeepWork_irabaj")}</AlertDialogTitle>
            <AlertDialogDescription>{t("chat/deepWorkModeSearches_bzo82a")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              {t("common/cancel_8mt5fw")}
            </AlertDialogClose>
            <Button disabled={deepWorkControl.disabled} onClick={confirmDeepWorkIntro}>
              {t("settings/enable_a4d9uv")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
});
